import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useConfig } from '../hooks/useConfig';
import { 
  Globe, Mail, Calendar, Cloud, FileSpreadsheet, FileText, CheckSquare, 
  Users, Video, MessageSquare, Plus, Search, ArrowLeft, Trash, 
  Download, Eye, RefreshCw, Lock, Unlock, ExternalLink, Check, AlertTriangle, 
  ChevronRight, FileImage, Layers, Trash2, Send, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// Icon utility helper based on mimetype
const getFileIcon = (mimeType: string) => {
  if (mimeType.includes('folder')) return <Layers className="w-5 h-5 text-amber-500 fill-amber-500/10" />;
  if (mimeType.includes('document')) return <FileText className="w-5 h-5 text-blue-500" />;
  if (mimeType.includes('spreadsheet')) return <FileSpreadsheet className="w-5 h-5 text-emerald-500" />;
  if (mimeType.includes('presentation')) return <Layers className="w-5 h-5 text-orange-500" />;
  if (mimeType.includes('image')) return <FileImage className="w-5 h-5 text-indigo-500" />;
  return <Cloud className="w-5 h-5 text-slate-500" />;
};

export const WorkspaceHub: React.FC = () => {
  const { user, authorizeWorkspace } = useAuth();
  const { getTileStyles, config, updateConfig } = useConfig();
  const globalStyles = getTileStyles();
  const menuStyles = getTileStyles("Menù");

  const [isWorkspaceEnabled, setIsWorkspaceEnabled] = useState(false);

  // Sync state with useConfig config when it changes
  useEffect(() => {
    if (config) {
      const hubNode = config.items?.find((item: any) => item.item === "Google Workspace Hub");
      const integrationNode = hubNode?.children?.find((child: any) => child.item === "Integrazione");
      const attivaVal = integrationNode?.attiva?.[0]?.userValue;
      setIsWorkspaceEnabled(attivaVal === true || attivaVal === "true");
    }
  }, [config]);

  const toggleWorkspaceEnabled = async (enabled: boolean) => {
    if (!config || !updateConfig) return;

    // Clone user config to avoid direct mutation
    const newConfig = JSON.parse(JSON.stringify(config));
    let hubNode = newConfig.items?.find((item: any) => item.item === "Google Workspace Hub");
    if (!hubNode) {
      hubNode = {
        item: "Google Workspace Hub",
        children: [
          {
            item: "Integrazione",
            attiva: [{ fieldType: "Boolean", userValue: enabled, defaultValue: false }]
          }
        ]
      };
      if (!newConfig.items) newConfig.items = [];
      newConfig.items.push(hubNode);
    } else {
      let integrationNode = hubNode.children?.find((child: any) => child.item === "Integrazione");
      if (!integrationNode) {
        integrationNode = {
          item: "Integrazione",
          attiva: [{ fieldType: "Boolean", userValue: enabled, defaultValue: false }]
        };
        if (!hubNode.children) hubNode.children = [];
        hubNode.children.push(integrationNode);
      } else {
        if (!integrationNode.attiva) {
          integrationNode.attiva = [{ fieldType: "Boolean", userValue: enabled, defaultValue: false }];
        } else {
          integrationNode.attiva[0].userValue = enabled;
        }
      }
    }

    setIsWorkspaceEnabled(enabled);
    const success = await updateConfig(newConfig);
    if (success) {
      if (enabled) {
        const storedToken = localStorage.getItem('google_access_token') || sessionStorage.getItem('google_access_token');
        const isAuth = localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
        if (storedToken && isAuth) {
          setAccessToken(storedToken);
          toast.success("Integrazione abilitata con successo usando la sessione esistente!");
        } else {
          toast.info("Integrazione abilitata. Connessione a Google Workspace in corso...");
          await handleAuthorize();
        }
      } else {
        // If disabling, clear local info
        setAccessToken(null);
        localStorage.removeItem('google_access_token');
        sessionStorage.removeItem('google_access_token');
        localStorage.removeItem('google_workspace_authorized');
        sessionStorage.removeItem('google_workspace_authorized');
        toast.info("Integrazione Google Workspace disattivata.");
      }
    } else {
      toast.error("Errore nel salvataggio della configurazione.");
      setIsWorkspaceEnabled(!enabled); // Rollback
    }
  };

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'mail' | 'calendar' | 'drive' | 'tasks' | 'contacts' | 'chat'>('overview');
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // States for API Data
  const [emails, setEmails] = useState<any[]>(() => {
    try {
      const cached = sessionStorage.getItem('workspace_hub_emails');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [events, setEvents] = useState<any[]>(() => {
    try {
      const cached = sessionStorage.getItem('workspace_hub_events');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [files, setFiles] = useState<any[]>(() => {
    try {
      const cached = sessionStorage.getItem('workspace_hub_files');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [currentFolder, setCurrentFolder] = useState<string>('root');
  const [folderHistory, setFolderHistory] = useState<string[]>([]);
  const [tasks, setTasks] = useState<any[]>(() => {
    try {
      const cached = sessionStorage.getItem('workspace_hub_tasks');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [contacts, setContacts] = useState<any[]>(() => {
    try {
      const cached = sessionStorage.getItem('workspace_hub_contacts');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  
  // States for Realtime Workspace Overview Stats
  const [workspaceStats, setWorkspaceStats] = useState<{
    gmail: { total: number; unread: number } | null;
    drive: { fileCount: number; usageBytes: number; limitBytes: number } | null;
    calendar: { upcomingCount: number } | null;
    tasks: { total: number; completed: number } | null;
    contacts: { total: number } | null;
  }>(() => {
    try {
      const cached = sessionStorage.getItem('workspace_hub_stats');
      return cached ? JSON.parse(cached) : {
        gmail: null,
        drive: null,
        calendar: null,
        tasks: null,
        contacts: null
      };
    } catch {
      return {
        gmail: null,
        drive: null,
        calendar: null,
        tasks: null,
        contacts: null
      };
    }
  });
  const [isStatsLoading, setIsStatsLoading] = useState(false);

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const loadWorkspaceStats = async () => {
    const token = accessToken || sessionStorage.getItem('google_access_token') || localStorage.getItem('google_access_token');
    const isAuthorized = isWorkspaceEnabled || localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
    if (!token || !isAuthorized) return;

    setIsStatsLoading(true);

    const fetchGmail = async () => {
      try {
        const [profile, unreadLabel] = await Promise.all([
          fetchGoogleAPI('https://gmail.googleapis.com/gmail/v1/users/me/profile', {}, true),
          fetchGoogleAPI('https://gmail.googleapis.com/gmail/v1/users/me/labels/UNREAD', {}, true)
        ]);
        return {
          total: profile?.messagesTotal || 0,
          unread: unreadLabel?.messagesUnread || unreadLabel?.messagesTotal || 0
        };
      } catch (e) {
        console.warn('Failed to load Gmail stats:', e);
        return { total: 0, unread: 0 };
      }
    };

    const fetchDrive = async () => {
      try {
        const aboutData = await fetchGoogleAPI('https://www.googleapis.com/drive/v3/about?fields=storageQuota', {}, true);
        let fileCount = 0;
        let pageToken = '';
        let pages = 0;
        try {
          do {
            const url = `https://www.googleapis.com/drive/v3/files?pageSize=1000&fields=nextPageToken,files(id)&pageToken=${pageToken}`;
            const driveData = await fetchGoogleAPI(url, {}, true);
            if (driveData && driveData.files) {
              fileCount += driveData.files.length;
              pageToken = driveData.nextPageToken || '';
            } else {
              break;
            }
            pages++;
          } while (pageToken && pages < 3);
        } catch (fileErr) {
          console.warn('Failed sorting files count:', fileErr);
        }

        return {
          fileCount,
          usageBytes: Number(aboutData?.storageQuota?.usage || 0),
          limitBytes: Number(aboutData?.storageQuota?.limit || 15 * 1024 * 1024 * 1024)
        };
      } catch (e) {
        console.warn('Failed to load Drive stats:', e);
        return { fileCount: 0, usageBytes: 0, limitBytes: 15 * 1024 * 1024 * 1024 };
      }
    };

    const fetchCalendar = async () => {
      try {
        const now = new Date();
        const next7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        const calendarData = await fetchGoogleAPI(`https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin=${now.toISOString()}&timeMax=${next7Days.toISOString()}&singleEvents=true`, {}, true);
        return {
          upcomingCount: calendarData?.items?.length || 0
        };
      } catch (e) {
        console.warn('Failed to load Calendar stats:', e);
        return { upcomingCount: 0 };
      }
    };

    const fetchTasks = async () => {
      try {
        const listsData = await fetchGoogleAPI('https://tasks.googleapis.com/v1/users/@me/lists', {}, true);
        if (listsData && listsData.items && listsData.items.length > 0) {
          const primaryListId = listsData.items[0].id;
          const tasksData = await fetchGoogleAPI(`https://tasks.googleapis.com/v1/lists/${primaryListId}/tasks?showCompleted=true&showHidden=true&maxResults=100`, {}, true);
          if (tasksData && tasksData.items) {
            return {
              total: tasksData.items.length,
              completed: tasksData.items.filter((t: any) => t.status === 'completed').length
            };
          }
        }
        return { total: 0, completed: 0 };
      } catch (e) {
        console.warn('Failed to load Tasks stats:', e);
        return { total: 0, completed: 0 };
      }
    };

    const fetchContacts = async () => {
      try {
        const contactsData = await fetchGoogleAPI('https://people.googleapis.com/v1/people/me/connections?personFields=names&pageSize=10', {}, true);
        return {
          total: contactsData?.totalItems || contactsData?.connections?.length || 0
        };
      } catch (e) {
        console.warn('Failed to load Contacts stats:', e);
        return { total: 0 };
      }
    };

    const [gmail, drive, calendar, tasks, contacts] = await Promise.all([
      fetchGmail(),
      fetchDrive(),
      fetchCalendar(),
      fetchTasks(),
      fetchContacts()
    ]);

    setWorkspaceStats({ gmail, drive, calendar, tasks, contacts });
    sessionStorage.setItem('workspace_hub_stats', JSON.stringify({ gmail, drive, calendar, tasks, contacts }));
    setIsStatsLoading(false);
  };

  // Custom picker selection state
  const [selectedFile, setSelectedFile] = useState<any | null>(null);
  const [filePreviewContent, setFilePreviewContent] = useState<any>(null);

  // Search queries
  const [searchQuery, setSearchQuery] = useState('');

  // Modals / Input State
  const [isNewEventModalOpen, setIsNewEventModalOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    start: '',
    end: '',
    description: '',
    location: '',
    meetLink: false
  });

  const [isSendMailModalOpen, setIsSendMailModalOpen] = useState(false);
  const [newMail, setNewMail] = useState({
    to: '',
    subject: '',
    body: ''
  });

  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');

  const [isNewContactModalOpen, setIsNewContactModalOpen] = useState(false);
  const [newContact, setNewContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  const [driveMenuCategory, setDriveMenuCategory] = useState<'my-drive' | 'shared' | 'starred' | 'recent'>('my-drive');

  // Load access token from sessionStorage or localStorage if Workspace is authorized
  useEffect(() => {
    const checkToken = () => {
      const isAuthorized = isWorkspaceEnabled || localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
      const token = sessionStorage.getItem('google_access_token') || localStorage.getItem('google_access_token');
      if (token && isAuthorized) {
        setAccessToken(token);
        return true;
      }
      return false;
    };

    // Check iniziale
    checkToken();

    // Se l'integrazione è abilitata nel profilo ma non è presente il token, 
    // facciamo un polling proattivo ogni secondo per rilevare istantaneamente il completamento
    // dell'autorizzazione da parte del popup indipendente (indipendentemente da postMessage / iframe isolati)
    let checkInterval: ReturnType<typeof setInterval> | null = null;
    if (isWorkspaceEnabled) {
      checkInterval = setInterval(() => {
        const found = checkToken();
        if (found && checkInterval) {
          clearInterval(checkInterval);
        }
      }, 1000);
    }

    // Configura i listener per la ricezione in tempo reale dei token (bypassando partizionamento iFrame/Popup)
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'OAUTH_AUTH_SUCCESS') {
        const token = event.data.session?.provider_token || event.data.session?.provider_access_token;
        const refreshToken = event.data.session?.provider_refresh_token;
        if (token) {
          sessionStorage.setItem('google_access_token', token);
          localStorage.setItem('google_access_token', token);
          localStorage.setItem('google_workspace_authorized', 'true');
          sessionStorage.setItem('google_workspace_authorized', 'true');
          if (refreshToken) {
            sessionStorage.setItem('google_refresh_token', refreshToken);
            localStorage.setItem('google_refresh_token', refreshToken);
          }
          setAccessToken(token);
          toast.success('Autorizzazione Google Workspace eseguita con successo!');
        }
      }
    };

    let authChannel: BroadcastChannel | null = null;
    try {
      authChannel = new BroadcastChannel('supabase_auth');
      authChannel.onmessage = (event) => {
        if (event.data?.type === 'OAUTH_AUTH_SUCCESS') {
          const token = event.data.session?.provider_token || event.data.session?.provider_access_token;
          const refreshToken = event.data.session?.provider_refresh_token;
          if (token) {
            sessionStorage.setItem('google_access_token', token);
            localStorage.setItem('google_access_token', token);
            localStorage.setItem('google_workspace_authorized', 'true');
            sessionStorage.setItem('google_workspace_authorized', 'true');
            if (refreshToken) {
              sessionStorage.setItem('google_refresh_token', refreshToken);
              localStorage.setItem('google_refresh_token', refreshToken);
            }
            setAccessToken(token);
            toast.success('Autorizzazione Google Workspace eseguita con successo!');
          }
        }
      };
    } catch (e) {
      console.error('WorkspaceHub: BroadcastChannel error:', e);
    }

    const handleStorageChange = (e: StorageEvent) => {
      if ((e.key === 'google_access_token' || e.key === 'google_workspace_authorized') && e.newValue) {
        const isAuth = isWorkspaceEnabled || localStorage.getItem('google_workspace_authorized') === 'true';
        const updatedToken = localStorage.getItem('google_access_token');
        if (updatedToken && isAuth) {
          setAccessToken(updatedToken);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorageChange);
      if (authChannel) authChannel.close();
      if (checkInterval) clearInterval(checkInterval);
    };
  }, [isWorkspaceEnabled]);

  // Fetch stats ONLY on overview tab
  useEffect(() => {
    if (accessToken && activeTab === 'overview') {
      const cached = sessionStorage.getItem('workspace_hub_stats');
      if (!cached) {
        loadWorkspaceStats();
      }
    }
  }, [accessToken, activeTab]);

  const handleAuthorize = async () => {
    if (authorizeWorkspace) {
      try {
        await authorizeWorkspace();
        // A short check interval to verify if the token was refreshed and authorized for Workspace
        const check = setInterval(() => {
          const isAuthorized = isWorkspaceEnabled || localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
          const updatedToken = sessionStorage.getItem('google_access_token') || localStorage.getItem('google_access_token');
          if (updatedToken && isAuthorized) {
            localStorage.setItem('google_workspace_authorized', 'true');
            sessionStorage.setItem('google_workspace_authorized', 'true');
            setAccessToken(updatedToken);
            clearInterval(check);
            toast.success('Autorizzazione Google Workspace eseguita con successo!');
          }
        }, 1000);
        setTimeout(() => clearInterval(check), 30000);
      } catch (err: any) {
        toast.error('Errore durante l\'autorizzazione guidata: ' + err.message);
      }
    } else {
      toast.error('Google Workspace provider helper non pronto.');
    }
  };

  // Google API generic request handler
  const fetchGoogleAPI = async (endpoint: string, options: RequestInit = {}, silent = false) => {
    const token = accessToken || sessionStorage.getItem('google_access_token') || localStorage.getItem('google_access_token');
    const isAuthorized = isWorkspaceEnabled || localStorage.getItem('google_workspace_authorized') === 'true' || sessionStorage.getItem('google_workspace_authorized') === 'true';
    if (!token || !isAuthorized) {
      if (!silent) {
        toast.error('Sessione Google non attiva o permessi mancanti. Per favore collega l\'account.');
      }
      setAccessToken(null);
      return null;
    }

    try {
      const res = await fetch(endpoint, {
        ...options,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options.headers,
        }
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('Google API Error:', err);
        if (res.status === 401) {
          sessionStorage.removeItem('google_access_token');
          localStorage.removeItem('google_access_token');
          // Non rimuoviamo google_workspace_authorized per preservare lo stato di collegamento
          setAccessToken(null);
          if (!silent) {
            toast.error('Sessione temporanea scaduta. Sarà rinfrescata al prossimo avvio o cliccando su Riconnetti.');
          }
        } else if (res.status === 403) {
          console.warn('WorkspaceHub: Permission error (403) on endpoint:', endpoint, err);
          const detailMsg = err.error?.message || err.message || '';
          if (!silent) {
            toast.error(`Integrazione non disponibile: ${detailMsg || 'permessi o scope mancanti.'}`);
          }
        } else {
          if (!silent) {
            toast.error(err.error?.message || 'Si è verificato un errore con le API Google.');
          }
        }
        return null;
      }

      if (res.status === 204) return true;
      return await res.json();
    } catch (e: any) {
      console.error('API execution failed:', e);
      if (!silent) {
        toast.error('Errore di connessione con le API Google.');
      }
      return null;
    }
  };

  // Gmail APIs
  const loadEmails = async () => {
    setIsLoading(true);
    const data = await fetchGoogleAPI('https://gmail.googleapis.com/gmail/v1/users/me/messages?maxResults=10');
    if (data && data.messages) {
      const messagesWithDetails = await Promise.all(
        data.messages.map(async (msg: any) => {
          const detail = await fetchGoogleAPI(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${msg.id}`);
          if (detail) {
            const getPartsStr = (headers: any[], name: string) => {
              return headers.find((h: any) => h.name.toLowerCase() === name.toLowerCase())?.value || '';
            };
            return {
              id: detail.id,
              snippet: detail.snippet,
              from: getPartsStr(detail.payload.headers, 'From'),
              subject: getPartsStr(detail.payload.headers, 'Subject'),
              date: getPartsStr(detail.payload.headers, 'Date'),
            };
          }
          return null;
        })
      );
      const filtered = messagesWithDetails.filter(Boolean);
      setEmails(filtered);
      sessionStorage.setItem('workspace_hub_emails', JSON.stringify(filtered));
    } else {
      setEmails([]);
      sessionStorage.setItem('workspace_hub_emails', JSON.stringify([]));
    }
    setIsLoading(false);
  };

  const handleSendMail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMail.to || !newMail.subject || !newMail.body) {
      toast.error('Tutti i campi della mail sono obbligatori.');
      return;
    }

    const emailLines = [
      `To: ${newMail.to}`,
      `Subject: ${newMail.subject}`,
      'Content-Type: text/plain; charset="UTF-8"',
      '',
      newMail.body
    ].join('\r\n');

    // Base64url encode safe for email send format
    const encodedEmail = btoa(unescape(encodeURIComponent(emailLines)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    const response = await fetchGoogleAPI('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
      method: 'POST',
      body: JSON.stringify({ raw: encodedEmail })
    });

    if (response) {
      toast.success('Email inviata con successo!');
      setIsSendMailModalOpen(false);
      setNewMail({ to: '', subject: '', body: '' });
      loadEmails();
    }
  };

  // Google Calendar APIs
  const loadEvents = async () => {
    setIsLoading(true);
    const now = new Date().toISOString();
    const data = await fetchGoogleAPI(`https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin=${now}&maxResults=10&singleEvents=true&orderBy=startTime`);
    if (data && data.items) {
      setEvents(data.items);
      sessionStorage.setItem('workspace_hub_events', JSON.stringify(data.items));
    } else {
      setEvents([]);
      sessionStorage.setItem('workspace_hub_events', JSON.stringify([]));
    }
    setIsLoading(false);
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEvent.title || !newEvent.start || !newEvent.end) {
      toast.error('Titolo, inizio e fine sono obbligatori.');
      return;
    }

    // Secure user intent confirmation
    const confirmed = window.confirm(`Confermi la creazione del seguente evento nel tuo Google Calendar?\n\nTitolo: ${newEvent.title}\nInizio: ${new Date(newEvent.start).toLocaleString()}`);
    if (!confirmed) return;

    const eventBody: any = {
      summary: newEvent.title,
      description: newEvent.description,
      location: newEvent.location,
      start: { dateTime: new Date(newEvent.start).toISOString() },
      end: { dateTime: new Date(newEvent.end).toISOString() },
    };

    if (newEvent.meetLink) {
      eventBody.conferenceData = {
        createRequest: {
          requestId: `meet-${Date.now()}`,
          conferenceSolutionKey: { type: 'hangoutsMeet' }
        }
      };
    }

    const endpoint = 'https://www.googleapis.com/calendar/v3/calendars/primary/events?conferenceDataVersion=1';
    const response = await fetchGoogleAPI(endpoint, {
      method: 'POST',
      body: JSON.stringify(eventBody)
    });

    if (response) {
      toast.success('Evento inserito con successo nel calendario!');
      setIsNewEventModalOpen(false);
      setNewEvent({ title: '', start: '', end: '', description: '', location: '', meetLink: false });
      loadEvents();
    }
  };

  const handleDeleteEvent = async (eventId: string, summary: string) => {
    const confirmed = window.confirm(`Sei sicuro di voler CANCELLARE l'evento "${summary}"?\nQuesta operazione è irreversibile.`);
    if (!confirmed) return;

    const response = await fetchGoogleAPI(`https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`, {
      method: 'DELETE',
    });

    if (response) {
      toast.success('Evento rimosso con successo.');
      loadEvents();
    }
  };

  // Google Drive APIs (Provides file browsing & selection, acting as standard Picker)
  const loadDriveFiles = async (folderId: string = 'root', category: 'my-drive' | 'shared' | 'starred' | 'recent' = 'my-drive') => {
    setIsLoading(true);
    setDriveMenuCategory(category);
    
    let query = `"${folderId}" in parents and trashed = false`;
    let orderBy = 'folder,name';
    
    if (category === 'shared') {
      if (folderId === 'root') {
        query = 'sharedWithMe = true and trashed = false';
      } else {
        query = `"${folderId}" in parents and trashed = false`;
      }
    } else if (category === 'starred') {
      if (folderId === 'root') {
        query = 'starred = true and trashed = false';
      } else {
        query = `"${folderId}" in parents and trashed = false`;
      }
    } else if (category === 'recent') {
      if (folderId === 'root') {
        query = 'trashed = false';
        orderBy = 'modifiedTime desc';
      } else {
        query = `"${folderId}" in parents and trashed = false`;
      }
    }

    const data = await fetchGoogleAPI(`https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name,mimeType,modifiedTime,size,webViewLink,iconLink)&orderBy=${orderBy}&pageSize=50`);
    if (data && data.files) {
      setFiles(data.files);
      setCurrentFolder(folderId);
      if (folderId === 'root' && category === 'my-drive') {
        sessionStorage.setItem('workspace_hub_files', JSON.stringify(data.files));
      }
    } else {
      setFiles([]);
      if (folderId === 'root' && category === 'my-drive') {
        sessionStorage.setItem('workspace_hub_files', JSON.stringify([]));
      }
    }
    setIsLoading(false);
  };

  const navigateToFolder = (folderId: string) => {
    setFolderHistory(prev => [...prev, currentFolder]);
    loadDriveFiles(folderId, driveMenuCategory);
  };

  const navigateBackFolder = () => {
    if (folderHistory.length === 0) return;
    const previous = folderHistory[folderHistory.length - 1];
    setFolderHistory(prev => prev.slice(0, -1));
    loadDriveFiles(previous, driveMenuCategory);
  };

  // Click on a file opens the Inline Picker Preview and reads Sheets / Docs API directly!
  const handleFileSelect = async (file: any) => {
    setSelectedFile(file);
    setFilePreviewContent(null);

    if (file.mimeType.includes('folder')) {
      navigateToFolder(file.id);
      return;
    }

    // Google Sheets live spreadsheet read
    if (file.mimeType.includes('spreadsheet')) {
      setIsLoading(true);
      const data = await fetchGoogleAPI(`https://sheets.googleapis.com/v4/spreadsheets/${file.id}/values/A1:H20`);
      if (data && data.values) {
        setFilePreviewContent({ type: 'sheets', values: data.values });
      }
      setIsLoading(false);
    }
    // Google Documents text blocks read
    else if (file.mimeType.includes('document')) {
      setIsLoading(true);
      const data = await fetchGoogleAPI(`https://docs.googleapis.com/v1/documents/${file.id}`);
      if (data && data.body?.content) {
        const textBlocks = data.body.content
          .map((c: any) => c.paragraph?.elements?.map((e: any) => e.textRun?.content).join(''))
          .filter(Boolean);
        setFilePreviewContent({ type: 'docs', text: textBlocks });
      }
      setIsLoading(false);
    }
  };

  // Google Tasks APIs
  const loadTasks = async () => {
    setIsLoading(true);
    // Find first tasklist
    const listsData = await fetchGoogleAPI('https://tasks.googleapis.com/v1/users/@me/lists');
    if (listsData && listsData.items && listsData.items.length > 0) {
      const primaryListId = listsData.items[0].id;
      const tasksData = await fetchGoogleAPI(`https://tasks.googleapis.com/v1/lists/${primaryListId}/tasks?showCompleted=true&showHidden=true`);
      if (tasksData && tasksData.items) {
        setTasks(tasksData.items);
        sessionStorage.setItem('workspace_hub_tasks', JSON.stringify(tasksData.items));
      } else {
        setTasks([]);
        sessionStorage.setItem('workspace_hub_tasks', JSON.stringify([]));
      }
    } else {
      setTasks([]);
      sessionStorage.setItem('workspace_hub_tasks', JSON.stringify([]));
    }
    setIsLoading(false);
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle) {
      toast.error('Il titolo è obbligatorio.');
      return;
    }

    const listsData = await fetchGoogleAPI('https://tasks.googleapis.com/v1/users/@me/lists');
    if (listsData && listsData.items && listsData.items.length > 0) {
      const primaryListId = listsData.items[0].id;
      const response = await fetchGoogleAPI(`https://tasks.googleapis.com/v1/lists/${primaryListId}/tasks`, {
        method: 'POST',
        body: JSON.stringify({ title: newTaskTitle })
      });

      if (response) {
        toast.success('Task aggiunto alla tua lista!');
        setNewTaskTitle('');
        setIsNewTaskModalOpen(false);
        loadTasks();
      }
    }
  };

  const handleToggleTaskComplete = async (taskId: string, currentStatus: string) => {
    const listsData = await fetchGoogleAPI('https://tasks.googleapis.com/v1/users/@me/lists');
    if (listsData && listsData.items && listsData.items.length > 0) {
      const primaryListId = listsData.items[0].id;
      const newStatus = currentStatus === 'completed' ? 'needsAction' : 'completed';
      
      const response = await fetchGoogleAPI(`https://tasks.googleapis.com/v1/lists/${primaryListId}/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({
          id: taskId,
          status: newStatus,
          // If completing, Google Tasks needs completed timestamp empty or present
          completed: newStatus === 'completed' ? new Date().toISOString() : null
        })
      });

      if (response) {
        toast.success(newStatus === 'completed' ? 'Attività completata!' : 'Attività riattivata.');
        loadTasks();
      }
    }
  };

  // Google Contacts (People API)
  const loadContacts = async () => {
    setIsLoading(true);
    const data = await fetchGoogleAPI('https://people.googleapis.com/v1/people/me/connections?personFields=names,emailAddresses,phoneNumbers,photos&pageSize=30');
    if (data && data.connections) {
      setContacts(data.connections);
      sessionStorage.setItem('workspace_hub_contacts', JSON.stringify(data.connections));
    } else {
      setContacts([]);
      sessionStorage.setItem('workspace_hub_contacts', JSON.stringify([]));
    }
    setIsLoading(false);
  };

  const handleCreateContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newContact.firstName || !newContact.lastName || (!newContact.email && !newContact.phone)) {
      toast.error('Inserisci almeno nome, cognome e un contatto (Email o Telefono).');
      return;
    }

    const contactBody = {
      names: [{ givenName: newContact.firstName, familyName: newContact.lastName }],
      emailAddresses: newContact.email ? [{ value: newContact.email, type: 'work' }] : [],
      phoneNumbers: newContact.phone ? [{ value: newContact.phone, type: 'work' }] : []
    };

    const response = await fetchGoogleAPI('https://people.googleapis.com/v1/people', {
      method: 'POST',
      body: JSON.stringify(contactBody)
    });

    if (response) {
      toast.success('Contatto aggiunto correttamente su Google Contacts!');
      setIsNewContactModalOpen(false);
      setNewContact({ firstName: '', lastName: '', email: '', phone: '' });
      loadContacts();
    }
  };

  // Tab change automations
  useEffect(() => {
    if (!accessToken) return;
    if (activeTab === 'mail') {
      const cached = sessionStorage.getItem('workspace_hub_emails');
      if (!cached && emails.length === 0) {
        loadEmails();
      }
    }
    if (activeTab === 'calendar') {
      const cached = sessionStorage.getItem('workspace_hub_events');
      if (!cached && events.length === 0) {
        loadEvents();
      }
    }
    if (activeTab === 'drive') {
      const cached = sessionStorage.getItem('workspace_hub_files');
      if (!cached && files.length === 0) {
        loadDriveFiles('root', 'my-drive');
      }
    }
    if (activeTab === 'tasks') {
      const cached = sessionStorage.getItem('workspace_hub_tasks');
      if (!cached && tasks.length === 0) {
        loadTasks();
      }
    }
    if (activeTab === 'contacts') {
      const cached = sessionStorage.getItem('workspace_hub_contacts');
      if (!cached && contacts.length === 0) {
        loadContacts();
      }
    }
  }, [activeTab, accessToken]);

  return (
    <div className="space-y-6">
      {/* Intestazione */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center p-5 bg-white border border-slate-100 rounded-3xl shadow-xs gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Google Workspace Hub</h1>
          <p className="text-slate-400 text-sm mt-0.5">Gestisci la suite dei tuoi servizi CPGEV  Bologna collegati.</p>
        </div>
        
        <div className="flex items-center gap-4 bg-slate-50 border border-slate-100 px-4 py-2.5 rounded-2xl">
          <div className="flex items-center gap-2">
            <button
              id="workspace-hub-toggle"
              type="button"
              onClick={() => toggleWorkspaceEnabled(!isWorkspaceEnabled)}
              className={cn(
                "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                isWorkspaceEnabled ? "bg-emerald-500" : "bg-red-500"
              )}
            >
              <span
                className={cn(
                  "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out",
                  isWorkspaceEnabled ? "translate-x-5" : "translate-x-0"
                )}
              />
            </button>
            <span className="text-xs font-black text-slate-700 select-none uppercase tracking-wider">
              Integrazione: {isWorkspaceEnabled ? <span className="text-emerald-600">ON</span> : <span className="text-red-500">OFF</span>}
            </span>
          </div>
          
          <div className="w-[1px] h-6 bg-slate-200" />

          {isWorkspaceEnabled && accessToken ? (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-600">
                <Unlock className="w-3.5 h-3.5" />
                <span>Attiva</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  sessionStorage.removeItem('workspace_hub_stats');
                  sessionStorage.removeItem('workspace_hub_emails');
                  sessionStorage.removeItem('workspace_hub_events');
                  sessionStorage.removeItem('workspace_hub_files');
                  sessionStorage.removeItem('workspace_hub_tasks');
                  sessionStorage.removeItem('workspace_hub_contacts');
                  
                  if (activeTab === 'overview') loadWorkspaceStats();
                  else if (activeTab === 'mail') loadEmails();
                  else if (activeTab === 'calendar') loadEvents();
                  else if (activeTab === 'drive') loadDriveFiles('root', 'my-drive');
                  else if (activeTab === 'tasks') loadTasks();
                  else if (activeTab === 'contacts') loadContacts();
                  
                  toast.success('Dati ricaricati in tempo reale!');
                }}
                className="px-2 py-0.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 font-bold text-[10px] uppercase tracking-wider rounded-md border border-emerald-100 transition inline-flex items-center gap-1 active:scale-95"
                title="Sincronizza nuovamente i dati Workspace ricaricandoli direttamente dalle API"
              >
                <RefreshCw className="w-2.5 h-2.5 transition-transform hover:rotate-180 duration-500" />
                <span>Aggiorna</span>
              </button>
              <button
                type="button"
                onClick={handleAuthorize}
                className="px-2 py-0.5 bg-blue-50 hover:bg-blue-100 text-blue-600 font-bold text-[10px] uppercase tracking-wider rounded-md border border-blue-100 transition active:scale-95"
                title="Sincronizza nuovamente o autorizza nuovi permessi Workspace"
              >
                Riconnetti
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-xs font-bold text-slate-400">
              <Lock className="w-3.5 h-3.5" />
              <span>Non attiva</span>
            </div>
          )}
        </div>
      </div>

      {!isWorkspaceEnabled ? (
        <div className="bg-white border border-red-100 rounded-3xl p-10 text-center flex flex-col items-center justify-center space-y-6 max-w-2xl mx-auto shadow-xs select-none">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center text-red-500">
            <Lock className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-bold text-slate-800">Integrazione Google Workspace Disattivata</h3>
            <p className="text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
              L'integrazione con Google Workspace non è abilitata nelle tue preferenze personali.
              Attiva l'interruttore in alto per sbloccare l'accesso e sincronizzare i servizi della GEV Bologna.
            </p>
          </div>
          <button 
            type="button"
            onClick={() => toggleWorkspaceEnabled(true)}
            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 font-bold text-white text-xs uppercase tracking-wider rounded-xl shadow-md transition"
          >
            Attiva Integrazione Ora
          </button>
        </div>
      ) : !accessToken ? (
        <div className="bg-white border border-amber-100 rounded-3xl p-10 text-center flex flex-col items-center justify-center space-y-6 max-w-2xl mx-auto shadow-xs select-none">
          <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center text-amber-500 animate-pulse">
            <AlertTriangle className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-bold text-slate-800">Connessione Google Workspace Richiesta</h3>
            <p className="text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
              L'integrazione è abilitata nelle preferenze utente, ma è necessario associare il tuo account Google istituzionale per recuperare in sicurezza le comunicazioni e i file personali.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <button 
              type="button"
              onClick={handleAuthorize}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 font-bold text-white text-xs uppercase tracking-wider rounded-xl shadow-md transition"
            >
              Collega Account Google Workspace
            </button>
            <button 
              type="button"
              onClick={() => toggleWorkspaceEnabled(false)}
              className="px-6 py-3 bg-slate-100 hover:bg-slate-200 font-bold text-slate-600 text-xs uppercase tracking-wider rounded-xl transition"
            >
              Disattiva Integrazione
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Menu Laterale Servizi */}
          <div className="bg-card border rounded-2xl p-4 flex flex-col space-y-2 h-fit" style={{ 
            backgroundColor: menuStyles.sfondo,
            borderColor: menuStyles.bordo_colore || '#e2e8f0',
            borderWidth: menuStyles.bordo_spessore || '1px',
            borderStyle: (menuStyles.bordo_tipo as any) || 'solid'
          }}>
            {[
              { id: 'overview', label: 'Panoramica', icon: <Globe className="w-4 h-4" /> },
              { id: 'mail', label: 'Gmail Business', icon: <Mail className="w-4 h-4" /> },
              { id: 'calendar', label: 'Google Calendario', icon: <Calendar className="w-4 h-4" /> },
              { id: 'drive', label: 'Drive & Picker', icon: <Cloud className="w-4 h-4" /> },
              { id: 'tasks', label: 'Google Tasks', icon: <CheckSquare className="w-4 h-4" /> },
              { id: 'contacts', label: 'Rubrica', icon: <Users className="w-4 h-4" /> },
              { id: 'chat', label: 'Meet & Chat Space', icon: <MessageSquare className="w-4 h-4" /> }
            ].map(item => {
              const isActive = activeTab === item.id;
              const isHovered = hoveredTab === item.id;
              return (
                <button 
                  key={item.id}
                  onClick={() => setActiveTab(item.id as any)}
                  onMouseEnter={() => setHoveredTab(item.id)}
                  onMouseLeave={() => setHoveredTab(null)}
                  className="flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition text-left cursor-pointer"
                  style={{
                    backgroundColor: isActive ? menuStyles.selected : isHovered ? menuStyles.mouseover : 'transparent',
                    color: isActive ? '#fff' : isHovered ? menuStyles.voci : menuStyles.unselected,
                    fontFamily: menuStyles.tipo_carattere,
                    fontSize: menuStyles.dimensione,
                    fontWeight: menuStyles.formato === 'bold' ? 'bold' : 'normal',
                  }}
                >
                  <div className="flex items-center gap-3">
                    {item.icon}
                    <span>{item.label}</span>
                  </div>
                  <ChevronRight className="w-4 h-4" />
                </button>
              );
            })}
          </div>

          {/* Area Principale Contenuti */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* PANORAMICA TAB */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-zinc-900 to-slate-800 text-white p-8 rounded-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-6 shadow-md border border-slate-700/20">
                  <div>
                    <h2 className="text-2xl font-bold">Workspace Pronto all'Azione</h2>
                    <p className="text-zinc-300 mt-2 text-sm max-w-md">
                      Accedi a documenti, ricevi mail, gestisci i verbali condivisi e mantieni sincronizzato il tuo calendario di associazione istituzionale.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button 
                      onClick={() => setActiveTab('calendar')}
                      className="px-4 py-2 bg-white text-zinc-900 rounded-xl font-bold text-xs hover:bg-zinc-100 transition shadow"
                    >
                      Nuovo Evento
                    </button>
                    <button 
                      onClick={() => setActiveTab('mail')}
                      className="px-4 py-2 bg-white/10 text-white rounded-xl font-bold text-xs hover:bg-white/20 transition border border-white/20"
                    >
                      Invia Mail
                    </button>
                  </div>
                </div>

                {/* RESOCONTO IN TEMPO REALE */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center whitespace-nowrap">
                    <h3 className="text-lg font-bold">Resoconto in Tempo Reale</h3>
                    <button 
                      onClick={loadWorkspaceStats}
                      disabled={isStatsLoading}
                      className="flex items-center gap-1 text-xs text-blue-600 font-bold hover:underline"
                    >
                      <RefreshCw className={`w-3 h-3 ${isStatsLoading ? 'animate-spin' : ''}`} />
                      Aggiorna statistiche
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* CARD GMAIL */}
                    <div className="bg-card border rounded-2xl p-5 flex flex-col justify-between space-y-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2.5 bg-red-500/10 text-red-500 rounded-xl">
                            <Mail className="w-5 h-5 animate-pulse" />
                          </div>
                          <div>
                            <h4 className="font-bold text-sm">Gmail Business</h4>
                            <p className="text-[10px] text-muted-foreground">InBOX istituzionale</p>
                          </div>
                        </div>
                        {workspaceStats.gmail ? (
                          workspaceStats.gmail.unread > 0 ? (
                            <span className="px-2 py-0.5 bg-red-500 text-white text-[10px] font-extrabold rounded-full">
                              {workspaceStats.gmail.unread} Nuovi
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-emerald-500/15 text-emerald-600 text-[10px] font-extrabold rounded-full">
                              Letto
                            </span>
                          )
                        ) : null}
                      </div>

                      <div className="space-y-1">
                        <div className="text-2xl font-black text-slate-800 tracking-tight">
                          {isStatsLoading ? (
                            <div className="h-7 w-20 bg-slate-100 animate-pulse rounded" />
                          ) : workspaceStats.gmail ? (
                            `${workspaceStats.gmail.total - workspaceStats.gmail.unread} / ${workspaceStats.gmail.total}`
                          ) : (
                            'collegamento...'
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground font-semibold">
                          Messaggi Letti / Totale Messaggi
                        </p>
                      </div>

                      <button 
                        onClick={() => setActiveTab('mail')}
                        className="text-xs font-bold text-blue-600 flex items-center gap-1 hover:underline pt-2 border-t w-fit"
                      >
                        Apri posta della segreteria <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>

                    {/* CARD DRIVE */}
                    <div className="bg-card border rounded-2xl p-5 flex flex-col justify-between space-y-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2.5 bg-blue-500/10 text-blue-500 rounded-xl">
                            <Cloud className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-bold text-sm">Google Drive</h4>
                            <p className="text-[10px] text-muted-foreground">Archivio verbali e fogli</p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-baseline">
                          <div className="text-2xl font-black text-slate-800 tracking-tight">
                            {isStatsLoading ? (
                              <div className="h-7 w-24 bg-slate-100 animate-pulse rounded" />
                            ) : workspaceStats.drive ? (
                              `${workspaceStats.drive.fileCount} file`
                            ) : (
                              'collegamento...'
                            )}
                          </div>
                          <span className="text-xs font-bold text-slate-500">
                            {isStatsLoading ? "" : workspaceStats.drive ? formatBytes(workspaceStats.drive.usageBytes) : ""}
                          </span>
                        </div>

                        {workspaceStats.drive && (
                          <div className="space-y-1">
                            <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                              <div 
                                className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                                style={{ 
                                  width: `${Math.min(100, Math.round((workspaceStats.drive.usageBytes / workspaceStats.drive.limitBytes) * 100))}%` 
                                }}
                              />
                            </div>
                            <div className="flex justify-between text-[10px] text-muted-foreground">
                              <span>Spazio occupato</span>
                              <span>Limite: {formatBytes(workspaceStats.drive.limitBytes)}</span>
                            </div>
                          </div>
                        )}
                        {!workspaceStats.drive && (
                          <p className="text-xs text-muted-foreground font-semibold">
                            Sfoglia o condividi files del direttivo.
                          </p>
                        )}
                      </div>

                      <button 
                        onClick={() => setActiveTab('drive')}
                        className="text-xs font-bold text-blue-600 flex items-center gap-1 hover:underline pt-2 border-t w-fit"
                      >
                        Esplora file server di sezione <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>

                    {/* CARD CALENDAR */}
                    <div className="bg-card border rounded-2xl p-5 flex flex-col justify-between space-y-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2.5 bg-emerald-500/10 text-emerald-500 rounded-xl">
                            <Calendar className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-bold text-sm">Google Calendar</h4>
                            <p className="text-[10px] text-muted-foreground">Pianificazione turni</p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <div className="text-2xl font-black text-slate-800 tracking-tight">
                          {isStatsLoading ? (
                            <div className="h-7 w-20 bg-slate-100 animate-pulse rounded" />
                          ) : workspaceStats.calendar ? (
                            `${workspaceStats.calendar.upcomingCount} impegni`
                          ) : (
                            'collegamento...'
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground font-semibold">
                          Eventi pianificati nei prossimi 7 giorni
                        </p>
                      </div>

                      <button 
                        onClick={() => setActiveTab('calendar')}
                        className="text-xs font-bold text-blue-600 flex items-center gap-1 hover:underline pt-2 border-t w-fit"
                      >
                        Mostra calendario eventi <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>

                    {/* CARD TASKS */}
                    <div className="bg-card border rounded-2xl p-5 flex flex-col justify-between space-y-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2.5 bg-amber-500/10 text-amber-500 rounded-xl">
                            <CheckSquare className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-bold text-sm">Google Tasks</h4>
                            <p className="text-[10px] text-muted-foreground">Cose da fare del direttivo</p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <div className="text-2xl font-black text-slate-800 tracking-tight">
                          {isStatsLoading ? (
                            <div className="h-7 w-20 bg-slate-100 animate-pulse rounded" />
                          ) : workspaceStats.tasks ? (
                            `${workspaceStats.tasks.completed} / ${workspaceStats.tasks.total}`
                          ) : (
                            'collegamento...'
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground font-semibold">
                          Attività completate / Totale attività pianificate
                        </p>
                      </div>

                      <button 
                        onClick={() => setActiveTab('tasks')}
                        className="text-xs font-bold text-blue-600 flex items-center gap-1 hover:underline pt-2 border-t w-fit"
                      >
                        Gestisci lista attività <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>

                    {/* CARD CONTACTS */}
                    <div className="bg-card border rounded-2xl p-5 flex flex-col justify-between space-y-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2.5 bg-indigo-500/10 text-indigo-500 rounded-xl">
                            <Users className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-bold text-sm">Contatti GEV</h4>
                            <p className="text-[10px] text-muted-foreground">Rubrica istituzionale del corpo</p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <div className="text-2xl font-black text-slate-800 tracking-tight">
                          {isStatsLoading ? (
                            <div className="h-7 w-20 bg-slate-100 animate-pulse rounded" />
                          ) : workspaceStats.contacts ? (
                            `${workspaceStats.contacts.total} GEV`
                          ) : (
                            'collegamento...'
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground font-semibold">
                          Contatti registrati nella rubrica condivisa
                        </p>
                      </div>

                      <button 
                        onClick={() => setActiveTab('contacts')}
                        className="text-xs font-bold text-blue-600 flex items-center gap-1 hover:underline pt-2 border-t w-fit"
                      >
                        Visualizza rubrica contatti <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>

                    {/* CARD MEET & CHAT */}
                    <div className="bg-card border rounded-2xl p-5 flex flex-col justify-between space-y-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2.5 bg-violet-500/10 text-violet-500 rounded-xl">
                            <Video className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-bold text-sm">Meet & Chat Space</h4>
                            <p className="text-[10px] text-muted-foreground">Videoconferenze e coordinamento</p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <div className="text-xs text-muted-foreground leading-relaxed">
                          La tua suite istituzionale ti permette di avviare istantaneamente incontri video con i colleghi e coordinare il presidio dal vivo.
                        </div>
                      </div>

                      <button 
                        onClick={() => setActiveTab('chat')}
                        className="text-xs font-bold text-blue-600 flex items-center gap-1 hover:underline pt-2 border-t w-fit"
                      >
                        Avvia chat o video Meet <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* FAQ Workspace Specifics */}
                <div className="bg-amber-500/5 border border-amber-500/10 rounded-2xl p-6 space-y-2">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-bold text-sm">Note sulle limitazioni e l'utilizzo</span>
                  </div>
                  <p className="text-xs text-amber-700/80 leading-relaxed">
                    Per motivi di sicurezza le modifice a file, gli appunti Keep e le pianificazioni chat sono gestite con token volatili non memorizzati permanentemente per preservare la privacy dell'istituzione. Le API di eliminazione e modifica richiedono sempre conferma esplicita per evitare eventi non voluti.
                  </p>
                </div>
              </div>
            )}

            {/* TAB MAIL */}
            {activeTab === 'mail' && (
              <div 
                className="bg-card border rounded-2xl p-6 space-y-6 animate-in fade-in duration-300"
                style={{
                  backgroundColor: globalStyles.sfondo,
                  borderColor: globalStyles.bordo_colore || '#e5e7eb',
                  borderWidth: globalStyles.bordo_spessore || '1px',
                  borderStyle: (globalStyles.bordo_tipo as any) || 'solid'
                }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4">
                  <h3 className="text-xl font-bold tracking-tight">In Arrivo (Gmail)</h3>
                  
                  <a 
                    href="https://mail.google.com" 
                    target="_blank" 
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-100 font-bold text-xs rounded-xl shadow-sm border border-slate-200 dark:border-zinc-700 transition"
                  >
                    <Mail className="w-4 h-4 text-red-500 animate-pulse" />
                    <span>Continua su Gmail</span>
                  </a>

                  <div className="flex gap-2 shrink-0">
                    <button 
                      onClick={loadEmails} 
                      className="p-2 bg-accent hover:bg-accent/80 rounded-xl transition"
                      disabled={isLoading}
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                    <button 
                      onClick={() => setIsSendMailModalOpen(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 hover:bg-blue-700 transition"
                    >
                      <Plus className="w-4 h-4" /> Compila Mail
                    </button>
                  </div>
                </div>

                {isLoading && emails.length === 0 ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : emails.length === 0 ? (
                  <div className="text-center py-12 border border-dashed rounded-xl space-y-2">
                    <Mail className="w-8 h-8 text-slate-300 mx-auto" />
                    <p className="text-xs text-muted-foreground">Nessuna email recente o casella vuota.</p>
                  </div>
                ) : (
                  <div className="divide-y border rounded-xl overflow-hidden bg-white dark:bg-zinc-950">
                    {emails.map((email) => (
                      <div key={email.id} className="p-4 hover:bg-accent/35 transition cursor-pointer flex flex-col space-y-1">
                        <div className="flex justify-between items-start">
                          <span className="text-xs font-bold truncate max-w-[180px] text-zinc-900 dark:text-zinc-100">{email.from}</span>
                          <span className="text-[10px] text-muted-foreground shrink-0">{new Date(email.date).toLocaleDateString()}</span>
                        </div>
                        <p className="text-xs font-semibold truncate text-zinc-800 dark:text-zinc-200">{email.subject}</p>
                        <p className="text-[11px] text-muted-foreground truncate leading-snug">{email.snippet}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TAB CALENDAR */}
            {activeTab === 'calendar' && (
              <div 
                className="bg-card border rounded-2xl p-6 space-y-6 animate-in fade-in duration-300"
                style={{
                  backgroundColor: globalStyles.sfondo,
                  borderColor: globalStyles.bordo_colore || '#e5e7eb',
                  borderWidth: globalStyles.bordo_spessore || '1px',
                  borderStyle: (globalStyles.bordo_tipo as any) || 'solid'
                }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4">
                  <h3 className="text-xl font-bold tracking-tight">Calendario Gesvol</h3>
                  
                  <a 
                    href="https://calendar.google.com" 
                    target="_blank" 
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-100 font-bold text-xs rounded-xl shadow-sm border border-slate-200 dark:border-zinc-700 transition"
                  >
                    <Calendar className="w-4 h-4 text-emerald-500 animate-pulse" />
                    <span>Continua su Google Calendar</span>
                  </a>

                  <div className="flex gap-2 shrink-0">
                    <button 
                      onClick={loadEvents} 
                      className="p-2 bg-accent hover:bg-accent/80 rounded-xl transition"
                      disabled={isLoading}
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                    <button 
                      onClick={() => setIsNewEventModalOpen(true)}
                      className="px-4 py-2 bg-emerald-600 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 hover:bg-emerald-700 transition"
                    >
                      <Plus className="w-4 h-4" /> Calendario Evento
                    </button>
                  </div>
                </div>

                {isLoading && events.length === 0 ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : events.length === 0 ? (
                  <div className="text-center py-12 border border-dashed rounded-xl space-y-2">
                    <Calendar className="w-8 h-8 text-slate-300 mx-auto" />
                    <p className="text-xs text-muted-foreground">Non ci sono eventi pianificati a breve termine.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {events.map((event) => {
                      const startStr = event.start?.dateTime ? new Date(event.start.dateTime).toLocaleString() : event.start?.date;
                      return (
                        <div key={event.id} className="p-4 border rounded-xl hover:shadow-sm bg-white dark:bg-zinc-950 flex justify-between items-center gap-4 transition">
                          <div className="space-y-1 min-w-0">
                            <p className="font-bold text-xs text-slate-800 dark:text-zinc-200">{event.summary}</p>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-emerald-500" /> {startStr}</span>
                              {event.location && <span className="truncate max-w-[200px]">📍 {event.location}</span>}
                            </div>
                            {event.hangoutLink && (
                              <a 
                                href={event.hangoutLink} 
                                target="_blank" 
                                rel="noreferrer"
                                className="flex items-center gap-1 text-[10px] text-blue-600 font-bold hover:underline"
                              >
                                <Video className="w-3 h-3" /> Collegamento Meet
                              </a>
                            )}
                          </div>
                          <button 
                            onClick={() => handleDeleteEvent(event.id, event.summary)}
                            className="p-1.5 hover:bg-red-50 hover:text-red-600 border border-transparent hover:border-red-100 rounded-lg text-slate-400 shrink-0 transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* TAB DRIVE & CUSTOM FILE PICKER */}
            {activeTab === 'drive' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <div 
                  className="bg-card border rounded-2xl p-6 space-y-6"
                  style={{
                    backgroundColor: globalStyles.sfondo,
                    borderColor: globalStyles.bordo_colore || '#e5e7eb',
                    borderWidth: globalStyles.bordo_spessore || '1px',
                    borderStyle: (globalStyles.bordo_tipo as any) || 'solid'
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4">
                    <div className="space-y-1">
                      <h3 className="text-xl font-bold tracking-tight">Google Picker & Sfoglia File</h3>
                      <p className="text-xs text-muted-foreground">Sfoglia in tempo reale i tuoi documenti cloud.</p>
                    </div>
                    
                    <a 
                      href="https://drive.google.com" 
                      target="_blank" 
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-100 font-bold text-xs rounded-xl shadow-sm border border-slate-200 dark:border-zinc-700 transition"
                    >
                      <Cloud className="w-4 h-4 text-blue-500 animate-pulse" />
                      <span>Continua su Google Drive</span>
                    </a>

                    <div className="flex gap-2 shrink-0">
                      {folderHistory.length > 0 && (
                        <button 
                          onClick={navigateBackFolder}
                          className="px-3 py-1.5 bg-accent hover:bg-accent/80 rounded-xl font-semibold text-xs flex items-center gap-1 transition"
                        >
                          <ArrowLeft className="w-3.5 h-3.5" /> Indietro
                        </button>
                      )}
                      <button 
                        onClick={() => loadDriveFiles(currentFolder, driveMenuCategory)} 
                        className="p-2 bg-accent hover:bg-accent/80 rounded-xl transition"
                        disabled={isLoading}
                      >
                        <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  </div>

                  {/* Replica Menù di Drive per Categoria */}
                  <div className="flex flex-wrap gap-2 border-b pb-4">
                    {[
                      { id: 'my-drive', label: 'I Miei File', icon: <Layers className="w-4 h-4" /> },
                      { id: 'shared', label: 'Condivisi con me', icon: <Users className="w-4 h-4" /> },
                      { id: 'starred', label: 'Preferiti', icon: <Check className="w-4 h-4 text-amber-500" /> },
                      { id: 'recent', label: 'Recenti', icon: <Clock className="w-4 h-4 text-violet-500" /> }
                    ].map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => {
                          setFolderHistory([]);
                          loadDriveFiles('root', cat.id as any);
                        }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                          driveMenuCategory === cat.id
                            ? 'bg-zinc-950 text-white dark:bg-zinc-850'
                            : 'bg-accent/50 text-slate-750 hover:bg-accent'
                        }`}
                      >
                        {cat.icon}
                        <span>{cat.label}</span>
                      </button>
                    ))}
                  </div>

                  {isLoading && files.length === 0 ? (
                    <div className="flex justify-center items-center py-12">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : files.length === 0 ? (
                    <div className="text-center py-12 border border-dashed rounded-xl space-y-2">
                      <Cloud className="w-8 h-8 text-slate-300 mx-auto" />
                      <p className="text-xs text-muted-foreground">Nessun file o cartella rilevato in questa directory.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {files.map((file) => (
                        <div 
                          key={file.id} 
                          onClick={() => handleFileSelect(file)}
                          className={`p-3.5 border rounded-xl hover:shadow-sm cursor-pointer transition flex items-center justify-between gap-3 ${selectedFile?.id === file.id ? 'border-zinc-900 bg-zinc-50 dark:bg-zinc-800' : 'bg-white dark:bg-zinc-950'}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {getFileIcon(file.mimeType)}
                            <div className="min-w-0">
                              <p className="text-xs font-bold truncate text-slate-800 dark:text-zinc-200">{file.name}</p>
                              <p className="text-[9px] text-muted-foreground">{new Date(file.modifiedTime).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* FILE PICKER INLINE DETAILED PREVIEW & API DEEP INTERACTION */}
                {selectedFile && !selectedFile.mimeType.includes('folder') && (
                  <div className="bg-card border rounded-2xl p-6 space-y-4 shadow-sm animate-in zoom-in duration-200">
                    <div className="flex justify-between items-center border-b pb-4">
                      <div className="flex items-center gap-2">
                        {getFileIcon(selectedFile.mimeType)}
                        <h4 className="font-bold text-sm text-slate-800 dark:text-zinc-200">{selectedFile.name}</h4>
                      </div>
                      <div className="flex gap-2">
                        <a 
                          href={selectedFile.webViewLink} 
                          target="_blank" 
                          rel="noreferrer"
                          className="px-3 py-1.5 bg-accent hover:bg-accent/80 text-xs font-bold rounded-xl flex items-center gap-1 shadow-sm transition"
                        >
                          Apri sul Web <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </div>

                    {isLoading ? (
                      <div className="flex justify-center items-center py-6">
                        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : filePreviewContent?.type === 'sheets' ? (
                      <div className="space-y-3">
                        <span className="text-[10px] uppercase font-bold text-emerald-600 bg-emerald-500/10 px-2.5 py-1 rounded-full">Anteprima Fogli di Lavoro Google Sped_Sheet</span>
                        <div className="overflow-x-auto border rounded-xl bg-white dark:bg-zinc-950">
                          <table className="w-full border-collapse text-left text-xs">
                            <tbody>
                              {filePreviewContent.values.map((row: string[], idx: number) => (
                                <tr key={idx} className="border-b hover:bg-accent/10">
                                  {row.map((cell: string, cellIdx: number) => (
                                    <td key={cellIdx} className="p-2 border-r font-mono last:border-r-0 text-[10px] whitespace-nowrap min-w-[100px]">{cell}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : filePreviewContent?.type === 'docs' ? (
                      <div className="space-y-3">
                        <span className="text-[10px] uppercase font-bold text-blue-600 bg-blue-500/10 px-2.5 py-1 rounded-full">Anteprima Contenuto Google Documenti</span>
                        <div className="p-6 border rounded-xl bg-white dark:bg-zinc-950 font-serif text-xs leading-relaxed space-y-4 max-h-[250px] overflow-y-auto">
                          {filePreviewContent.text.map((t: string, idx: number) => (
                            <p key={idx}>{t}</p>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6 text-xs text-muted-foreground border rounded-xl border-dashed">
                        Selezionato file "{selectedFile.name}". Clicca sul tasto sopra per aprilo sul Drive originale.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* TAB TASKS */}
            {activeTab === 'tasks' && (
              <div 
                className="bg-card border rounded-2xl p-6 space-y-6 animate-in fade-in duration-300"
                style={{
                  backgroundColor: globalStyles.sfondo,
                  borderColor: globalStyles.bordo_colore || '#e5e7eb',
                  borderWidth: globalStyles.bordo_spessore || '1px',
                  borderStyle: (globalStyles.bordo_tipo as any) || 'solid'
                }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4">
                  <h3 className="text-xl font-bold tracking-tight">Google Tasks & Attività</h3>
                  
                  <a 
                    href="https://mail.google.com/tasks/canvas" 
                    target="_blank" 
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-100 font-bold text-xs rounded-xl shadow-sm border border-slate-200 dark:border-zinc-700 transition"
                  >
                    <CheckSquare className="w-4 h-4 text-blue-500 animate-pulse" />
                    <span>Continua su Google Tasks</span>
                  </a>

                  <div className="flex gap-2 shrink-0">
                    <button 
                      onClick={loadTasks} 
                      className="p-2 bg-accent hover:bg-accent/80 rounded-xl transition"
                      disabled={isLoading}
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                    <button 
                      onClick={() => setIsNewTaskModalOpen(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 hover:bg-blue-700 transition"
                    >
                      <Plus className="w-4 h-4" /> Nuovo Task
                    </button>
                  </div>
                </div>

                {isLoading && tasks.length === 0 ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : tasks.length === 0 ? (
                  <div className="text-center py-12 border border-dashed rounded-xl space-y-2">
                    <CheckSquare className="w-8 h-8 text-slate-300 mx-auto" />
                    <p className="text-xs text-muted-foreground">Non ci sono attività sulla tua lista temporanea.</p>
                  </div>
                ) : (
                  <div className="divide-y border rounded-xl overflow-hidden bg-white dark:bg-zinc-950">
                    {tasks.map((task) => (
                      <div 
                        key={task.id} 
                        className="px-4 py-3.5 hover:bg-accent/20 flex items-center justify-between gap-4 transition"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <button 
                            onClick={() => handleToggleTaskComplete(task.id, task.status)}
                            className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition ${task.status === 'completed' ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 hover:border-zinc-900 bg-white'}`}
                          >
                            {task.status === 'completed' && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                          </button>
                          <span className={`text-xs truncate text-slate-700 dark:text-zinc-200 ${task.status === 'completed' ? 'line-through text-muted-foreground' : 'font-semibold'}`}>
                            {task.title}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TAB RUBRICA CONTACTS */}
            {activeTab === 'contacts' && (
              <div 
                className="bg-card border rounded-2xl p-6 space-y-6 animate-in fade-in duration-300"
                style={{
                  backgroundColor: globalStyles.sfondo,
                  borderColor: globalStyles.bordo_colore || '#e5e7eb',
                  borderWidth: globalStyles.bordo_spessore || '1px',
                  borderStyle: (globalStyles.bordo_tipo as any) || 'solid'
                }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4">
                  <h3 className="text-xl font-bold tracking-tight">Contatti Google Bologna</h3>
                  
                  <a 
                    href="https://contacts.google.com" 
                    target="_blank" 
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-100 font-bold text-xs rounded-xl shadow-sm border border-slate-200 dark:border-zinc-700 transition"
                  >
                    <Users className="w-4 h-4 text-cyan-500 animate-pulse" />
                    <span>Continua su Google Contatti</span>
                  </a>

                  <div className="flex gap-2 shrink-0">
                    <button 
                      onClick={loadContacts} 
                      className="p-2 bg-accent hover:bg-accent/80 rounded-xl transition"
                      disabled={isLoading}
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                    <button 
                      onClick={() => setIsNewContactModalOpen(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 hover:bg-blue-700 transition"
                    >
                      <Plus className="w-4 h-4" /> Aggiungi Contatto
                    </button>
                  </div>
                </div>

                {isLoading && contacts.length === 0 ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="text-center py-12 border border-dashed rounded-xl space-y-2">
                    <Users className="w-8 h-8 text-slate-300 mx-auto" />
                    <p className="text-xs text-muted-foreground">Rubrica temporaneamente vuota.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {contacts.map((c) => {
                      const name = c.names?.[0]?.displayName || 'Utente Sconosciuto';
                      const email = c.emailAddresses?.[0]?.value || '';
                      const phone = c.phoneNumbers?.[0]?.value || '';
                      const photoUrl = c.photos?.[0]?.url || '';

                      return (
                        <div key={c.resourceName} className="p-3 border rounded-xl flex items-center gap-3 bg-white dark:bg-zinc-950 shadow-sm hover:shadow-md transition">
                          <div className="w-10 h-10 rounded-full border shrink-0 bg-slate-50 overflow-hidden flex items-center justify-center">
                            {photoUrl ? (
                              <img src={photoUrl} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                            ) : (
                              <Users className="w-5 h-5 text-slate-300" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-800 dark:text-zinc-200 truncate">{name}</p>
                            {email && <p className="text-[10px] text-muted-foreground truncate">{email}</p>}
                            {phone && <p className="text-[10px] text-muted-foreground truncate">{phone}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* TAB MEET, CHAT & INTERACTIVE WEBHOOK NOTIFICATIONS */}
            {activeTab === 'chat' && (
              <div 
                className="bg-card border rounded-2xl p-6 space-y-6 animate-in fade-in duration-300"
                style={{
                  backgroundColor: globalStyles.sfondo,
                  borderColor: globalStyles.bordo_colore || '#e5e7eb',
                  borderWidth: globalStyles.bordo_spessore || '1px',
                  borderStyle: (globalStyles.bordo_tipo as any) || 'solid'
                }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4">
                  <div className="space-y-1">
                    <h3 className="text-xl font-bold tracking-tight">Spazi Chat & Google Rinnovi Meet</h3>
                    <p className="text-xs text-muted-foreground">Pianificazione canali di chat GEV, webhook e integrazioni in tempo reale.</p>
                  </div>
                  
                  <a 
                    href="https://chat.google.com" 
                    target="_blank" 
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-zinc-100 font-bold text-xs rounded-xl shadow-sm border border-slate-200 dark:border-zinc-700 transition"
                  >
                    <MessageSquare className="w-4 h-4 text-emerald-500 animate-pulse" />
                    <span>Continua su Google Chat</span>
                  </a>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-5 border rounded-2xl bg-white dark:bg-zinc-950 flex flex-col space-y-3">
                    <div className="flex items-center gap-2 text-blue-600 font-bold text-sm">
                      <Video className="w-4 h-4" /> Generatore Meet
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Crea istantaneamente una stanza di video conferenza Google Meet collegata al tuo account istituzionale con codice di accesso automatico.
                    </p>
                    <button 
                      onClick={() => {
                        toast.success('Pianificazione stanza Meet avviata!');
                        setActiveTab('calendar');
                        setIsNewEventModalOpen(true);
                        setNewEvent(p => ({ ...p, title: 'Video Conferenza Rapida GEV', meetLink: true }));
                      }}
                      className="px-4 py-2 bg-blue-600 text-white font-bold text-xs rounded-xl self-start hover:bg-blue-700 transition shadow mt-auto"
                    >
                      Genera Stanza Meet
                    </button>
                  </div>

                  <div className="p-5 border rounded-2xl bg-white dark:bg-zinc-950 flex flex-col space-y-3">
                    <div className="flex items-center gap-2 text-amber-600 font-bold text-sm">
                      <MessageSquare className="w-4 h-4" /> Spazi Chat & Webhook
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Sfrutta gli spazi Chat di Google per allertamento soci in tempo reale per allerte meteo. Configura Webhooks per notificare l'app in base agli iscritti.
                    </p>
                    <div className="p-3 border bg-accent/25 rounded-lg text-[10px] font-mono leading-snug break-all text-slate-600 dark:text-zinc-400">
                      POST https://chat.googleapis.com/v1/spaces/SPACE_ID/messages
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* MODAL NUOVO EVENTO CALENDARIO */}
      {isNewEventModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-zinc-900 border rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold mb-4">Nuovo Evento Google Calendar</h3>
            <form onSubmit={handleCreateEvent} className="space-y-4">
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase text-muted-foreground">Titolo Evento</label>
                <input 
                  type="text" 
                  value={newEvent.title} 
                  onChange={(e) => setNewEvent(p => ({ ...p, title: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-xl text-xs" 
                  required 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase text-muted-foreground">Orario Inizio</label>
                  <input 
                    type="datetime-local" 
                    value={newEvent.start} 
                    onChange={(e) => setNewEvent(p => ({ ...p, start: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-xl text-xs" 
                    required 
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase text-muted-foreground">Orario Fine</label>
                  <input 
                    type="datetime-local" 
                    value={newEvent.end} 
                    onChange={(e) => setNewEvent(p => ({ ...p, end: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-xl text-xs" 
                    required 
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase text-muted-foreground">Luogo</label>
                <input 
                  type="text" 
                  value={newEvent.location} 
                  onChange={(e) => setNewEvent(p => ({ ...p, location: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-xl text-xs" 
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase text-muted-foreground">Descrizione</label>
                <textarea 
                  value={newEvent.description} 
                  onChange={(e) => setNewEvent(p => ({ ...p, description: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-xl text-xs h-20 resize-none" 
                />
              </div>
              <div className="flex items-center gap-2 py-2">
                <input 
                  type="checkbox" 
                  id="meet_link" 
                  checked={newEvent.meetLink}
                  onChange={(e) => setNewEvent(p => ({ ...p, meetLink: e.target.checked }))}
                  className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="meet_link" className="text-xs font-semibold text-slate-700">Genera ed allega Stanza Google Meet</label>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button 
                  type="button" 
                  onClick={() => setIsNewEventModalOpen(false)}
                  className="px-4 py-2 hover:bg-accent border rounded-xl font-bold text-xs"
                >
                  Annulla
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-md"
                >
                  Incolla ed Inserisci
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL NUOVA MAIL INVIATA */}
      {isSendMailModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-zinc-900 border rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold mb-4">Componi Mail di Associazione</h3>
            <form onSubmit={handleSendMail} className="space-y-4">
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase text-muted-foreground">Destinatario (Email)</label>
                <input 
                  type="email" 
                  value={newMail.to} 
                  placeholder="destinatario@gevbologna.org"
                  onChange={(e) => setNewMail(p => ({ ...p, to: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-xl text-xs" 
                  required 
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase text-muted-foreground">Oggetto</label>
                <input 
                  type="text" 
                  value={newMail.subject} 
                  onChange={(e) => setNewMail(p => ({ ...p, subject: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-xl text-xs" 
                  required 
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase text-muted-foreground">Testo Messaggio</label>
                <textarea 
                  value={newMail.body} 
                  onChange={(e) => setNewMail(p => ({ ...p, body: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-xl text-xs h-36 font-sans resize-none" 
                  required 
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button 
                  type="button" 
                  onClick={() => setIsSendMailModalOpen(false)}
                  className="px-4 py-2 hover:bg-accent border rounded-xl font-bold text-xs"
                >
                  Annulla
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-blue-605 text-white font-bold text-xs rounded-xl shadow-md bg-blue-600 hover:bg-blue-700 flex items-center gap-1.5"
                >
                  <Send className="w-3.5 h-3.5" /> Invia Proposta
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL NUOVO TASK */}
      {isNewTaskModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-zinc-900 border rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold mb-4">Nuova Attività Google Tasks</h3>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase text-muted-foreground">Descrizione Attività</label>
                <input 
                  type="text" 
                  value={newTaskTitle} 
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  className="w-full px-4 py-2 border rounded-xl text-xs" 
                  placeholder="Es. Revisionare furgone di zona"
                  required 
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button 
                  type="button" 
                  onClick={() => setIsNewTaskModalOpen(false)}
                  className="px-4 py-2 hover:bg-accent border rounded-xl font-bold text-xs"
                >
                  Annulla
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-md"
                >
                  Aggiungi Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL NUOVO CONTATTO */}
      {isNewContactModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-zinc-900 border rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold mb-4">Nuovo Contatto Rubrica</h3>
            <form onSubmit={handleCreateContact} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase text-muted-foreground">Nome</label>
                  <input 
                    type="text" 
                    value={newContact.firstName} 
                    onChange={(e) => setNewContact(p => ({ ...p, firstName: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-xl text-xs" 
                    required 
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase text-muted-foreground">Cognome</label>
                  <input 
                    type="text" 
                    value={newContact.lastName} 
                    onChange={(e) => setNewContact(p => ({ ...p, lastName: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-xl text-xs" 
                    required 
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase text-muted-foreground">Indirizzo Email</label>
                <input 
                  type="email" 
                  value={newContact.email} 
                  onChange={(e) => setNewContact(p => ({ ...p, email: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-xl text-xs" 
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase text-muted-foreground">Numero di Telefono</label>
                <input 
                  type="tel" 
                  value={newContact.phone} 
                  onChange={(e) => setNewContact(p => ({ ...p, phone: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-xl text-xs" 
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button 
                  type="button" 
                  onClick={() => setIsNewContactModalOpen(false)}
                  className="px-4 py-2 hover:bg-accent border rounded-xl font-bold text-xs"
                >
                  Annulla
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-md"
                >
                  Salva Contatto
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
